import { type ReactNode } from "react";
import { NumberRoll } from "./NumberRoll";

export interface BarRow {
  id: string;
  label: ReactNode;
  value: number;
  display?: string;
  displayValue?: number;
  format?: (v: number) => string;
  meta?: string;
  color?: string;
  onClick?: () => void;
}

export function BarList({ rows, max }: { rows: BarRow[]; max?: number }) {
  const peak = max ?? Math.max(1, ...rows.map((r) => r.value));
  // Bars render at their real width immediately. macOS shows data at its
  // settled value; a bar growing from zero performs the arrival and, like a
  // digit rolling up, misrepresents the value while it travels. Width still
  // transitions when the value itself changes.

  return (
    <div style={{ display: "grid", gap: 2 }}>
      {rows.map((r) => {
        const pct = Math.max(1.5, (r.value / peak) * 100);
        const tint = r.color ?? "var(--c-accent)";
        const Row = r.onClick ? "button" : "div";
        return (
          <Row
            key={r.id}
            onClick={r.onClick}
            style={{
              position: "relative",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: "var(--ui-space-3)",
              width: "100%", border: "none", background: "transparent",
              font: "inherit", textAlign: "left",
              cursor: r.onClick ? "pointer" : "default",
              padding: "7px var(--ui-space-2)",
              borderRadius: "var(--ui-control-radius)",
              overflow: "hidden",
              minHeight: 32,
            }}
          >
            <span
              aria-hidden
              style={{
                position: "absolute", insetInlineStart: 0, top: 0, bottom: 0,
                width: `${pct}%`,
                background: tint,
                opacity: 0.18,
                borderRadius: "var(--ui-control-radius)",
                transition: "width var(--ui-dur) var(--ui-ease)",
              }}
            />
            <span dir="auto" style={{
              position: "relative", minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              fontSize: "var(--ui-text-body)", color: "var(--c-t1)", fontWeight: 500,
            }}>{r.label}</span>
            <span className="tabnum" style={{
              position: "relative", display: "flex", alignItems: "baseline", gap: 8,
              flexShrink: 0, fontSize: "var(--ui-text-body)", color: "var(--c-t1)", fontWeight: 600,
            }}>
              {r.displayValue != null
                ? <NumberRoll value={r.displayValue} format={r.format} />
                : (r.display ?? r.value.toLocaleString())}
              {r.meta && (
                <span style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", fontWeight: 400 }}>
                  {r.meta}
                </span>
              )}
            </span>
          </Row>
        );
      })}
    </div>
  );
}
